/**
 * How a goal's next-action state is said, in one place.
 *
 * The browser's copy of `NO_NEXT_STEP_LABEL` in `@planner/core`, restated for the same reason
 * `LIMIT_MARGIN` and the role ladder are: the bundle deliberately does not depend on core, and a
 * word is cheaper to mirror than Drizzle is to ship. `goalVocabMirrorsCore.spec.ts` compares the
 * two, so a rename in core fails a test here rather than in front of a reader.
 *
 * The same reasoning as `taskVocab.ts` and `habitVocab.ts`. This one exists because the identical
 * `needsNextAction` fact was drawn as "no next action" on the goals banner and its callout, and as
 * "no next step" on goal detail and the week's reflection — one orange pill, two names, on screens
 * read together. Three personas across three rounds each stopped to check whether they were two
 * different conditions.
 */
export const NO_NEXT_STEP_LABEL = 'no next step';

/**
 * How a "Show all …" link says which goals it counted.
 *
 * Today and the Reflection both ended a list of goals with the same sentence over two different
 * sets. On the aspirational planner's account Today read *"Show all 8 goals"* and the Reflection,
 * of the same nine goals on the same afternoon, *"Show all 9 goals"* — because Today drops goals
 * that are not live (`goal.live`, and rightly: a pause is a decision, and nagging about it argues
 * with the user) while the review lists everything it reports on. Two counts, one undifferentiated
 * word, and no way for a reader flipping between the screens to know which of the two was lying.
 *
 * Neither count changes. What changes is that the smaller one says what it left out. `'active'`
 * is the status name the rest of the UI already uses — the goal a `Resume` press makes live again
 * is made `active` — and it is exactly `LIVE_GOAL_STATUSES`, which `goalCountLabel.spec.ts` pins:
 * add a fifth status to the pursued ones and the word has to be revisited there rather than
 * quietly becoming the same defect spelled differently.
 *
 * The residue is deliberate and worth naming: "Show all 8 active goals" and "Show all 9 goals"
 * are two sentences, and a reader flipping between the screens still sees 8 and 9. That is the
 * account — eight goals being pursued out of nine — and the only way to print one number on both
 * screens would be to change what one of them counts, which is the fix that makes the paused goal
 * either nag or vanish. Saying which set was counted is the honest version.
 */
export function showAllGoalsLabel(count: number, counted: 'active' | 'every'): string {
  return counted === 'active' ? `Show all ${count} active goals` : `Show all ${count} goals`;
}
